import {
  ExcelGridCellTone,
  ExcelGridColumn,
  ExcelGridFilterState,
  ExcelGridRow,
  ExcelGridSelectOption,
  ExcelGridSortState,
} from "../models/excel-grid.models";

const DEFAULT_MIN_WIDTH = 112;
const DEFAULT_MAX_WIDTH = 280;
const HEADER_PADDING_WIDTH = 36;
const CELL_PADDING_WIDTH = 30;
const CHAR_WIDTH = 7.2;

export const toCellText = (value: unknown): string => {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value);
};

export const getCellTone = (column: ExcelGridColumn, value: unknown): ExcelGridCellTone => {
  if (!column.serviceColumn) {
    return "neutral";
  }

  const normalized = toCellText(value).trim().toLowerCase();
  if (/^(yes|y|true)(\b|[^a-z])/.test(normalized)) {
    return "yes";
  }

  if (/^(no|n|false)(\b|[^a-z])/.test(normalized)) {
    return "no";
  }

  return "neutral";
};

export const getColumnWidths = (columns: ExcelGridColumn[], rows: ExcelGridRow[]): Record<string, number> =>
  columns.reduce<Record<string, number>>((widths, column) => {
    const minWidth = column.minWidth ?? DEFAULT_MIN_WIDTH;
    const maxWidth = column.maxWidth ?? DEFAULT_MAX_WIDTH;
    const labelWidth = column.label.length * CHAR_WIDTH + HEADER_PADDING_WIDTH;
    const contentWidth = getTypicalTextLength(rows.map((row) => toCellText(row[column.key]))) * CHAR_WIDTH;

    widths[column.key] = clamp(Math.ceil(Math.max(labelWidth, contentWidth + CELL_PADDING_WIDTH)), minWidth, maxWidth);
    return widths;
  }, {});

export const moveSelection = (
  rowIndex: number,
  columnIndex: number,
  rowDelta: number,
  columnDelta: number,
  rowsLength: number,
  columnsLength: number,
) => ({
  rowIndex: clamp(rowIndex + rowDelta, 0, Math.max(rowsLength - 1, 0)),
  columnIndex: clamp(columnIndex + columnDelta, 0, Math.max(columnsLength - 1, 0)),
});

export const getFilteredRows = (
  rows: ExcelGridRow[],
  columns: ExcelGridColumn[],
  filters: ExcelGridFilterState,
): ExcelGridRow[] => {
  const activeFilters = columns
    .map((column) => ({ column, query: filters[column.key]?.trim().toLowerCase() ?? "" }))
    .filter(({ query }) => query.length > 0);

  if (activeFilters.length === 0) {
    return rows;
  }

  return rows.filter((row) =>
    activeFilters.every(({ column, query }) => doesCellMatchFilter(column, row[column.key], query)),
  );
};

export const getSortedRows = (
  rows: ExcelGridRow[],
  sortState: ExcelGridSortState | null,
  columns: ExcelGridColumn[] = [],
): ExcelGridRow[] => {
  if (!sortState) {
    return rows;
  }

  const sortColumn = columns.find((column) => column.key === sortState.columnKey);
  return [...rows].sort((left, right) => {
    const leftValue = sortColumn?.getSortValue ? sortColumn.getSortValue(left, sortColumn) : left[sortState.columnKey];
    const rightValue = sortColumn?.getSortValue
      ? sortColumn.getSortValue(right, sortColumn)
      : right[sortState.columnKey];
    const result = compareCellValues(leftValue, rightValue);
    return sortState.direction === "asc" ? result : -result;
  });
};

export const getVisibleRows = (
  rows: ExcelGridRow[],
  columns: ExcelGridColumn[],
  filters: ExcelGridFilterState,
  sortState: ExcelGridSortState | null,
): ExcelGridRow[] => getSortedRows(getFilteredRows(rows, columns, filters), sortState, columns);

export const hasActiveFilters = (filters: ExcelGridFilterState): boolean =>
  Object.values(filters).some((value) => value.trim().length > 0);

export function findExcelGridSelectOptionByValue(
  options: ExcelGridSelectOption[],
  value: string,
): ExcelGridSelectOption | null {
  const lookupKey = toExcelGridSelectLookupKey(value);

  if (!lookupKey) {
    return null;
  }

  return options.find((option) => toExcelGridSelectLookupKey(option.value) === lookupKey) ?? null;
}

function doesCellMatchFilter(column: ExcelGridColumn, value: unknown, query: string): boolean {
  const cellText = toCellText(value).trim().toLowerCase();

  if (column.filterMatch === "wholeWords") {
    return doesTextContainWholeWords(cellText, query);
  }

  return cellText.includes(query);
}

function doesTextContainWholeWords(cellText: string, query: string): boolean {
  const normalizedCellWords = normalizeWordsForFilter(cellText);
  const normalizedQueryWords = normalizeWordsForFilter(query);

  if (!normalizedQueryWords) {
    return false;
  }

  return ` ${normalizedCellWords} `.includes(` ${normalizedQueryWords} `);
}

function normalizeWordsForFilter(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function getTypicalTextLength(values: string[]): number {
  if (values.length === 0) {
    return 0;
  }

  const lengths = values
    .map((value) => Math.max(...value.split(/\r?\n/).map((line) => line.length), 0))
    .sort((left, right) => left - right);
  const percentileIndex = Math.min(lengths.length - 1, Math.floor(lengths.length * 0.85));

  return lengths[percentileIndex] ?? 0;
}

function compareCellValues(left: unknown, right: unknown): number {
  const leftText = toCellText(left).trim();
  const rightText = toCellText(right).trim();
  const leftNumber = Number(leftText.replaceAll(",", ""));
  const rightNumber = Number(rightText.replaceAll(",", ""));

  if (leftText && rightText && Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }

  return leftText.localeCompare(rightText, undefined, { numeric: true, sensitivity: "base" });
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function toExcelGridSelectLookupKey(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

export const formatUndefinedCellToNA = (value: string | number | undefined) => {
  if (value === undefined || value === "") {
    return "N/A";
  }

  return String(value);
};
